import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Input, CustomSelect, LoadingButton } from '@redpanda/ui';
import { FormFieldWithError } from './forms';
import { useDialogClickOutside, useModalScrollLock } from '../hooks';
import { toast } from 'react-hot-toast';
import { bankAccountService } from '../services/api';

interface BankTransaction {
  id?: string;
  transactionDate: string;
  description: string;
  transactionType: string;
  amount: number;
  reference?: string;
}

interface BankTransactionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  transaction?: BankTransaction | null;
  onTransactionCreated?: (transaction: BankTransaction) => void;
  onTransactionUpdated?: (transaction: BankTransaction) => void;
}

interface FormData {
  transactionDate: string;
  description: string;
  transactionType: string;
  amount: number;
  reference: string;
}

const BankTransactionDialog: React.FC<BankTransactionDialogProps> = ({
  isOpen,
  onClose,
  accountId,
  transaction,
  onTransactionCreated,
  onTransactionUpdated
}) => {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch, getValues, control } = useForm<FormData>({
    defaultValues: {
      transactionDate: new Date().toISOString().split('T')[0],
      description: '',
      transactionType: '',
      amount: 0,
      reference: ''
    }
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);


  // 鎖定背景滾動
  useModalScrollLock(isOpen);

  const handleClose = () => {
    setSubmitError(null);
    reset();
    onClose();
  };

  // 點擊外部關閉 dialog
  const handleBackgroundClick = useDialogClickOutside(handleClose);

  // 設置表單初始值
  useEffect(() => {
    if (isOpen) {
      if (transaction) {
        // 編輯模式 - 設定所有欄位值
        setValue('transactionDate', new Date(transaction.transactionDate).toISOString().split('T')[0]);
        setValue('description', transaction.description);
        setValue('transactionType', transaction.transactionType); // 設定交易類型
        // 金額永遠用正數顯示（負數轉正數），類型決定方向
        setValue('amount', Math.abs(transaction.amount));
        setValue('reference', transaction.reference || '');
      } else {
        // 新增模式 - 重置表單
        reset({
          transactionDate: new Date().toISOString().split('T')[0],
          description: '',
          transactionType: '',
          amount: 0,
          reference: ''
        });
      }
    }
  }, [isOpen, transaction, setValue, reset]);

  // 交易類型選項
  const transactionTypeOptions = [
    { value: '', label: t('bankTransactions.dialog.selectType') },
    { value: 'DEPOSIT', label: t('bankTransactions.types.DEPOSIT') },
    { value: 'WITHDRAWAL', label: t('bankTransactions.types.WITHDRAWAL') },
    { value: 'TRANSFER_IN', label: t('bankTransactions.types.TRANSFER_IN') },
    { value: 'TRANSFER_OUT', label: t('bankTransactions.types.TRANSFER_OUT') },
    { value: 'FEE', label: t('bankTransactions.types.FEE') },
    { value: 'INTEREST', label: t('bankTransactions.types.INTEREST') }
  ];

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setSubmitError(null);

    try {
      const transactionData = {
        transactionDate: new Date(data.transactionDate).toISOString(),
        description: data.description,
        transactionType: data.transactionType,
        amount: Number(data.amount),
        reference: data.reference || undefined
      };

      if (transaction) {
        // 更新現有交易 - 目前使用本地更新
        const updatedTransaction = { ...transaction, ...transactionData };
        onTransactionUpdated?.(updatedTransaction);
        toast.success(t('common.messages.updateSuccess'));
      } else {
        // 創建新交易
        const response = await bankAccountService.createBankTransaction(accountId, transactionData);
        if (response.data?.data) {
          onTransactionCreated?.(response.data.data);
          toast.success(t('common.messages.createSuccess'));
        } else {
          throw new Error(t('bankTransactions.dialog.serverError'));
        }
      }

      handleClose();
    } catch (error: any) {
      console.error('儲存交易記錄失敗:', error);
      setSubmitError(error.response?.data?.message || t('bankTransactions.dialog.error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {transaction ? t('bankTransactions.dialog.editTitle') : t('bankTransactions.dialog.addTitle')}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <FormFieldWithError
            label={t('bankTransactions.dialog.date')}
            required={true}
            error={errors.transactionDate?.message}
            htmlFor="transactionDate"
          >
            <Input
              id="transactionDate"
              type="date"
              {...register('transactionDate', { required: t('common.forms.validation.required') })}
              variant={errors.transactionDate ? "error" : "default"}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('bankTransactions.dialog.description')}
            required={true}
            error={errors.description?.message}
            htmlFor="description"
          >
            <Input
              id="description"
              {...register('description', { required: t('common.forms.validation.required') })}
              variant={errors.description ? "error" : "default"}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('bankTransactions.dialog.type')}
            required={true}
            error={errors.transactionType?.message}
            htmlFor="transactionType"
          >
            <Controller
              name="transactionType"
              control={control}
              rules={{ required: t('common.forms.validation.required') }}
              defaultValue={transaction?.transactionType || ''}
              render={({ field }) => (
                <CustomSelect
                  id="transactionType"
                  options={transactionTypeOptions}
                  value={field.value || ''}
                  onChange={field.onChange}
                  variant={errors.transactionType ? "error" : "default"}
                />
              )}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('bankTransactions.dialog.amount')}
            required={true}
            error={errors.amount?.message}
            htmlFor="amount"
          >
            <Input
              id="amount"
              type="text"
              {...register('amount', {
                required: t('common.forms.validation.required'),
                setValueAs: (value) => {
                  const num = parseFloat(value);
                  return isNaN(num) ? 0 : num;
                },
                min: { value: 0.01, message: t('bankTransactions.dialog.amountMin') }
              })}
              variant={errors.amount ? "error" : "default"}
              placeholder={t('bankTransactions.dialog.amountPlaceholder')}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('bankTransactions.dialog.reference')}
            error={errors.reference?.message}
            htmlFor="reference"
          >
            <Input
              id="reference"
              {...register('reference')}
              variant={errors.reference ? "error" : "default"}
            />
          </FormFieldWithError>

          {submitError && (
            <div className="text-red-600 text-sm mt-2">{submitError}</div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
            >
              {t('common.actions.cancel')}
            </button>
            <LoadingButton
              type="submit"
              loading={loading}
              className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
            >
              {transaction ? t('common.actions.update') : t('common.actions.create')}
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankTransactionDialog;